import type { ChartImage } from '../types/chartTypes';

export type PrintChartAspect = 'wide' | 'square';

type PrintChartImageGridProps<K extends string> = {
  charts?: Partial<Record<K, ChartImage>>;
  order: readonly K[];
  aspectByKey?: Partial<Record<K, PrintChartAspect>>;
  className?: string;
  fitPage?: boolean;
};

const ASPECT_CLASS: Record<PrintChartAspect, string> = {
  // Fixed boxes keep mixed chart exports from resizing the print grid as images decode.
  wide: 'aspect-[2/1]',
  square: 'aspect-square'
};

export function PrintChartImageGrid<K extends string>({ charts, order, aspectByKey, className = '', fitPage = false }: PrintChartImageGridProps<K>) {
  if (!charts) return null;

  // Full-page print grids should use the available page area, not the fixed
  // interactive chart aspect. One chart fills the panel, two charts split it,
  // four-chart reports keep the historical 2x2 layout, and six-chart reports use 3x2.
  const fitPageTracks = order.length === 1 ? 'grid-cols-1 grid-rows-1' : order.length === 2 ? 'grid-cols-2 grid-rows-1' : order.length > 4 ? 'grid-cols-3 grid-rows-2' : 'grid-cols-2 grid-rows-2';
  const flowTracks = order.length === 1 ? 'grid-cols-1' : 'grid-cols-2';
  const gridClass = fitPage ? `grid h-full min-h-0 ${fitPageTracks} content-stretch gap-1 overflow-hidden p-1 ${className}` : `grid ${flowTracks} content-start gap-1 p-1 ${className}`;
  const cellBaseClass = fitPage ? 'flex min-h-0 overflow-hidden break-inside-avoid border border-slate-200 bg-white p-0.5' : 'flex overflow-hidden break-inside-avoid border border-slate-200 bg-white p-0.5';

  return (
    <div className={gridClass}>
      {order.map(key => {
        const chart = charts[key];
        if (!chart) return null;

        const aspect = aspectByKey?.[key] ?? 'wide';
        const src = `data:${chart.mime};base64,${chart.base64}`;

        return (
          <div key={key} className={`${cellBaseClass} ${fitPage ? '' : ASPECT_CLASS[aspect]}`}>
            {/* Use contain so Plotly's own title, axis labels, and legend are never cropped by the print cell. */}
            <img src={src} alt={`${key} chart`} loading="eager" decoding="sync" className="h-full w-full object-contain rounded-none" />
          </div>
        );
      })}
    </div>
  );
}
